/**
 * Notification Center page route.
 *
 * Full-page notification history: searchable, filterable, paginated.
 * Includes notification preferences settings in a collapsible section.
 */

import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { NotificationCenter } from '../../features/notifications/NotificationCenter'
import { NotificationSettings } from '../../features/notifications/NotificationSettings'

function SettingsIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden="true">
      <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  )
}

function NotificationsPage() {
  const [showSettings, setShowSettings] = useState(false)

  return (
    <div className="p-6">
      <div className="max-w-3xl mx-auto">
        {/* Settings toggle */}
        <div className="flex justify-end mb-4">
          <button
            type="button"
            onClick={() => setShowSettings((prev) => !prev)}
            className="
              inline-flex items-center gap-1.5 px-3 py-1.5
              rounded-[var(--radius-md)] text-sm font-medium
              text-[var(--color-text-secondary)]
              hover:bg-[var(--color-bg-secondary)] transition-colors
            "
            data-testid="settings-toggle"
          >
            <SettingsIcon className="h-4 w-4" />
            {showSettings ? 'Hide settings' : 'Notification settings'}
          </button>
        </div>

        {/* Settings panel */}
        {showSettings && (
          <div className="mb-6 p-4 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-primary)]">
            <NotificationSettings />
          </div>
        )}

        {/* Notification Center */}
        <NotificationCenter />
      </div>
    </div>
  )
}

export const Route = createFileRoute('/_authenticated/notifications')({
  component: NotificationsPage,
})
